// ✅ src/pages/VehicleV2/TyreMaintenanceDetailsForVehicleLogBook.jsx
// React equivalent for ASPX: TyreDetailsForvehiclelogbook.aspx  (INFRA SCREEN : B-13/2)
//
// ASPX flow matched (from code-behind TyreDetailsForvehiclelogbook.aspx.cs):
// - Branch dropdown from usp_BindBranch, then auto-select + disable based on Session["mainvehicleid"]
// - VehicleCode/RegNo/Name auto-filled + disabled (logbook context)
// - Search runs: usp_Get_AllTyreDetails
//     usp_Get_AllTyreDetails
//       <branchId>,
//       '<TyreSerialNo>%','<FromDate>','<ToDate>',
//       '<VehicleName>%','<VehicleRegNo>%','<VehicleCode>%',
//       '<TyreStatus>',
//       '<TyreModel>%','<TyreSize>%','<TyreMake>%'
//
// ✅ Backend mount (expected):
//   app.use("/api/tyreMaintenanceDetails", tyreMaintenanceDetailsRouter);
//
// Expected APIs:
//   GET  /api/tyreMaintenanceDetails/init?mainVehicleId=123&branchId=8&branchType=HO&loginUserId=37
//     -> { success:true, branches:[{label,value}], userLock:{ branchId, vehicleCode, vehicleRegNo, vehicleName, disableVehicleFields:true } }
//
//   GET  /api/tyreMaintenanceDetails/list?branchId=8&tyreStatus=Process&fromDate=2026-02-01&toDate=2026-02-20&
//        vehicleCode=ABC&vehicleRegNo=XX&vehicleName=YYY&tyreSerialNo=&tyreModel=&tyreSize=&tyreMake=&
//        page=1&pageSize=10
//     -> { success:true, rows:[...], total:123 }
//
// Optional (only if you want autocomplete like ASPX AutoCompleteExtender):
//   GET /api/tyreMaintenanceDetails/ac/vehicleCode?prefix=...
//   GET /api/tyreMaintenanceDetails/ac/vehicleRegNo?prefix=...
//   GET /api/tyreMaintenanceDetails/ac/vehicleName?prefix=...
//   GET /api/tyreMaintenanceDetails/ac/tyreSerialNo?prefix=...

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { FaSearch, FaArrowLeft, FaFilter, FaInfoCircle } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/tyreMaintenanceLogBook";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

// HTML date (yyyy-mm-dd) -> dd/MM/yyyy (ASPX uses dd/MM/yyyy textboxes)
const toDDMMYYYY = (yyyyMmDd) => {
  const s = toStr(yyyyMmDd);
  if (!s) return "";
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}/${y}`;
};

// dd/MM/yyyy -> yyyy-mm-dd (to keep React date inputs consistent if API returns dd/MM)
const ddmmyyyyToYmd = (ddmmyyyy) => {
  const s = toStr(ddmmyyyy);
  if (!s) return "";
  const parts = s.split("/");
  if (parts.length !== 3) return "";
  const [d, m, y] = parts;
  if (!d || !m || !y) return "";
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
};

const pick = (row, keys, def = "") => {
  for (const k of keys) {
    const v = row?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return def;
};

export default function TyreMaintenanceDetailsForVehicleLogBook() {
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const qs = useMemo(() => new URLSearchParams(search), [search]);

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

   const storedBranchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";
  const loginUserId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  // logbook context (ASPX uses Session["mainvehicleid"] / Session["mainvehicletyreid"])
  const mainVehicleId = toStr(state?.mainVehicleId || qs.get("mainVehicleId") || "");
  const mainVehicleTyreId = toStr(state?.mainVehicleTyreId || qs.get("mainVehicleTyreId") || "");

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  const [branches, setBranches] = useState([]);

  const [userLock, setUserLock] = useState({
    branchId: storedBranchId || "Select",
    vehicleCode: "",
    vehicleRegNo: "",
    vehicleName: "",
    disableVehicleFields: false,
    disableBranch: false,
  });

  // ✅ mirrors ASPX fields
  const [filters, setFilters] = useState({
    branchId: storedBranchId || "Select",
    tyreStatus: "Select", // Process/Scrap/Select
    fromDate: "", // yyyy-mm-dd (input type=date)
    toDate: "", // yyyy-mm-dd
    vehicleCode: "",
    vehicleRegNo: "",
    vehicleName: "",
    tyreSerialNo: "", // hidden in ASPX
    tyreModel: "", // hidden in ASPX
    tyreSize: "", // hidden in ASPX
    tyreMake: "", // hidden in ASPX
  });

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;
  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const branchOptions = useMemo(() => {
    return (branches || [])
      .map((b) => ({
        value: toStr(b.value ?? b.BranchId ?? b.branchid ?? b.fld_branchid),
        label: toStr(b.label ?? b.BranchName ?? b.branchname ?? b.fld_BranchName),
      }))
      .filter((o) => o.value || o.label);
  }, [branches]);

  const handleChange = (e) =>
    setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));

  /* ---------------- init ---------------- */
  const loadInit = async () => {
    try {
      const params = new URLSearchParams();
      params.set("branchId", storedBranchId || "8");
      params.set("branchType", branchType || "HO");
      params.set("loginUserId", loginUserId || "");
      if (mainVehicleId) params.set("mainVehicleId", mainVehicleId);
      if (mainVehicleTyreId) params.set("mainVehicleTyreId", mainVehicleTyreId);

      const { data } = await axios.get(`${API_BASE}/init?${params.toString()}`);
      if (!data?.success) throw new Error(data?.message || "Init failed");

      setBranches(data.branches || []);

      const lock = data.userLock || {};
      const lockBranchId = toStr(lock.branchId || lock.BranchId || lock.branchid || filters.branchId);

      setUserLock({
        branchId: lockBranchId || filters.branchId,
        vehicleCode: toStr(lock.vehicleCode),
        vehicleRegNo: toStr(lock.vehicleRegNo),
        vehicleName: toStr(lock.vehicleName),
        disableVehicleFields: !!lock.disableVehicleFields,
        disableBranch: lock.disableBranch ?? true, // ASPX disables branch in logbook
      });

      // apply lock values into filters (ASPX auto-fills and disables)
      setFilters((p) => ({
        ...p,
        branchId: lockBranchId || p.branchId,
        vehicleCode: toStr(lock.vehicleCode || p.vehicleCode),
        vehicleRegNo: toStr(lock.vehicleRegNo || p.vehicleRegNo),
        vehicleName: toStr(lock.vehicleName || p.vehicleName),
      }));
    } catch (err) {
      toast.error(err?.message || "Failed to load init");
    }
  };

  /* ---------------- list ---------------- */
  const buildListUrl = (f, pg = page, ps = pageSize) => {
    const params = new URLSearchParams();

    // keep "Select" as ASPX-like for branch/status when not selected
    params.set("branchId", isSelect(f.branchId) ? "Select" : toStr(f.branchId));
    params.set("tyreStatus", isSelect(f.tyreStatus) ? "Select" : toStr(f.tyreStatus));

    // ASPX passes dd/MM/yyyy textboxes -> we convert date input to dd/MM/yyyy
    params.set("fromDate", f.fromDate ? toDDMMYYYY(f.fromDate) : "");
    params.set("toDate", f.toDate ? toDDMMYYYY(f.toDate) : "");

    // ASPX adds "%" wildcard on these values; backend should do that
    params.set("vehicleCode", toStr(f.vehicleCode));
    params.set("vehicleRegNo", toStr(f.vehicleRegNo));
    params.set("vehicleName", toStr(f.vehicleName));

    params.set("tyreSerialNo", toStr(f.tyreSerialNo));
    params.set("tyreModel", toStr(f.tyreModel));
    params.set("tyreSize", toStr(f.tyreSize));
    params.set("tyreMake", toStr(f.tyreMake));

    // logbook ids if needed
    if (mainVehicleId) params.set("mainVehicleId", mainVehicleId);
    if (mainVehicleTyreId) params.set("mainVehicleTyreId", mainVehicleTyreId);

    // pagination
    params.set("page", String(pg));
    params.set("pageSize", String(ps));

    return `${API_BASE}/list?${params.toString()}`;
  };

  const loadList = async (f = filters, pg = page, ps = pageSize) => {
    setLoading(true);
    try {
      const url = buildListUrl(f, pg, ps);
      const { data } = await axios.get(url);
      if (!data?.success) throw new Error(data?.message || "Failed to load list");

      setRows(data.rows || []);
      setTotal(Number(data.total ?? (data.rows || []).length) || 0);
      setPage(Number(data.page || pg) || pg);
      setPageSize(Number(data.pageSize || ps) || ps);
    } catch (err) {
      setRows([]);
      setTotal(0);
      toast.error(err?.message || "List fetch failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInit().then(() => {
      // after init (lock values), load first page
      loadList(
        {
          ...filters,
          // try to honor lock immediately (if init returns)
          branchId: userLock.branchId || filters.branchId,
          vehicleCode: userLock.vehicleCode || filters.vehicleCode,
          vehicleRegNo: userLock.vehicleRegNo || filters.vehicleRegNo,
          vehicleName: userLock.vehicleName || filters.vehicleName,
        },
        1,
        pageSize
      );
      setPage(1);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- normalized rows for table ---------------- */
  const normRows = useMemo(() => {
    return (rows || []).map((r, idx) => {
      const tyreId = pick(r, ["fld_Tyreid", "TyreId", "tyreId"]);
      const tyreSerialHidden = pick(r, ["fld_Tyreserialno", "fld_Tyreserialno", "tyreSerialNo"]);
      const tyreSerial = pick(r, ["fld_tyreserailno", "TyreSerialNo", "tyreSerialNo"], tyreSerialHidden);

      return {
        sno: idx + 1 + (page - 1) * pageSize,
        tyreId,
        tyreSerial,
        vehicleRegNo: pick(r, ["fld_vehicleregno", "VehicleRegNo", "vehicleregno", "regNo"]),
        fittingDate: pick(r, ["fld_fittingdate", "FittingDate", "fittingDate"]),
        fittingHrKm: pick(r, ["fld_fittingkmhr", "FittingHrKm", "fittingHrKm"]),
        removingDate: pick(r, ["fld_removingdate", "RemovingDate", "removingDate"]),
        removingHrKm: pick(r, ["fld_RemovingKmhr", "RemovingHrKm", "removingHrKm"]),
        life: pick(r, ["fld_runninghrkm", "Life", "life"]),
        status: pick(r, ["fld_status", "Status", "status"]),
        _row: r,
      };
    });
  }, [rows, page, pageSize]);

  /* ---------------- actions ---------------- */
  const handleSearch = () => {
    setPage(1);
    loadList(filters, 1, pageSize);
  };

  const handleClear = () => {
    // keep lock values like ASPX (vehicle fields disabled/filled)
    const locked = {
      branchId: userLock.branchId || "Select",
      vehicleCode: userLock.vehicleCode || "",
      vehicleRegNo: userLock.vehicleRegNo || "",
      vehicleName: userLock.vehicleName || "",
    };

    setFilters((p) => ({
      ...p,
      branchId: locked.branchId,
      tyreStatus: "Select",
      fromDate: "",
      toDate: "",
      vehicleCode: locked.vehicleCode,
      vehicleRegNo: locked.vehicleRegNo,
      vehicleName: locked.vehicleName,
      tyreSerialNo: "",
      tyreModel: "",
      tyreSize: "",
      tyreMake: "",
    }));

    toast.info("Filters cleared");
  };

  const totalPages = useMemo(() => {
    const t = Number(total || 0);
    const ps = Number(pageSize || 10);
    return ps > 0 ? Math.max(1, Math.ceil(t / ps)) : 1;
  }, [total, pageSize]);

  const goPrev = () => {
    const next = Math.max(1, page - 1);
    setPage(next);
    loadList(filters, next, pageSize);
  };
  const goNext = () => {
    const next = Math.min(totalPages, page + 1);
    setPage(next);
    loadList(filters, next, pageSize);
  };

  // ASPX ButtonField CommandName="View" on Tyre SerialNo:
  // we navigate to a details/view page (you can rename route)
  const handleViewTyre = (r) => {
    const tyreId = r?.tyreId;
    const tyreSerialNo = r?.tyreSerial;
    const params = new URLSearchParams();
    if (tyreId) params.set("tyreId", String(tyreId));
    if (tyreSerialNo) params.set("tyreSerialNo", String(tyreSerialNo));
    if (mainVehicleId) params.set("mainVehicleId", String(mainVehicleId));
    if (mainVehicleTyreId) params.set("mainVehicleTyreId", String(mainVehicleTyreId));
    navigate(`/vehicle/TyreMaintenanceView?${params.toString()}`);
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">TYRE MAINTENANCE DETAILS</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-5">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Tyre Maintenance Details
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {total || normRows.length} Records
              </span>

              <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                INFRA SCREEN : B-13/2
              </span>

              {loading && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTERS
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <label className={labelClass}>Branch</label>
                  <select
                    name="branchId"
                    value={filters.branchId}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!!userLock.disableBranch}
                  >
                    <option value="Select">Select</option>
                    {branchOptions.map((b) => (
                      <option key={`${b.value}-${b.label}`} value={b.value}>
                        {b.label || b.value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    name="tyreStatus"
                    value={filters.tyreStatus}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    <option value="Process">Process</option>
                    <option value="Scrap">Scrap</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code</label>
                  <input
                    type="text"
                    name="vehicleCode"
                    value={filters.vehicleCode}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Vehicle Code"
                    disabled={!!userLock.disableVehicleFields}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Reg No</label>
                  <input
                    type="text"
                    name="vehicleRegNo"
                    value={filters.vehicleRegNo}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Vehicle Reg No"
                    disabled={!!userLock.disableVehicleFields}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Name</label>
                  <input
                    type="text"
                    name="vehicleName"
                    value={filters.vehicleName}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Vehicle Name"
                    disabled={!!userLock.disableVehicleFields}
                  />
                </div>

                {/* Hidden in ASPX (kept for API compatibility; toggle visible later if you want) */}
                <div className="hidden">
                  <label className={labelClass}>Tyre Serial No</label>
                  <input
                    type="text"
                    name="tyreSerialNo"
                    value={filters.tyreSerialNo}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div className="hidden">
                  <label className={labelClass}>Tyre Model</label>
                  <input
                    type="text"
                    name="tyreModel"
                    value={filters.tyreModel}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div className="hidden">
                  <label className={labelClass}>Tyre Size</label>
                  <input
                    type="text"
                    name="tyreSize"
                    value={filters.tyreSize}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div className="hidden">
                  <label className={labelClass}>Tyre Make</label>
                  <input
                    type="text"
                    name="tyreMake"
                    value={filters.tyreMake}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 gap-4">
                <button
                  onClick={handleClear}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium"
                  disabled={loading}
                >
                  Clear All Filters
                </button>

                <div className="flex gap-4">
                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-md`}
                    disabled={loading}
                  >
                    <FaSearch /> Search
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {normRows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  No Records Found
                </h3>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-center">S.No</th>
                      <th className="px-4 py-3 text-center">Tyre SerialNo</th>
                      <th className="px-4 py-3 text-center">Vehicle RegNo</th>
                      <th className="px-4 py-3 text-center">Fitting Date</th>
                      <th className="px-4 py-3 text-center">Fitting Hour/ KM</th>
                      <th className="px-4 py-3 text-center">Removing Date</th>
                      <th className="px-4 py-3 text-center">Removing Hour/ KM</th>
                      <th className="px-4 py-3 text-center">Life</th>
                      <th className="px-4 py-3 text-center">Retread / Original</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {normRows.map((r, idx) => (
                      <tr
                        key={`${r.tyreId || r.tyreSerial || "row"}-${idx}`}
                        className={`${isDark ? "bg-gray-800" : "bg-white"} ${
                          isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-4 py-3 text-center">{r.sno}</td>

                        {/* ✅ ASPX link button field (View) */}
                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleViewTyre(r)}
                            className={`underline font-semibold ${
                              isDark ? "text-blue-200 hover:text-blue-100" : "text-blue-700 hover:text-blue-900"
                            }`}
                            title="View tyre details"
                          >
                            {r.tyreSerial || "-"}
                          </button>
                        </td>

                        <td className="px-4 py-3 text-center">{r.vehicleRegNo}</td>
                        <td className="px-4 py-3 text-center">{r.fittingDate}</td>
                        <td className="px-4 py-3 text-center">{r.fittingHrKm}</td>
                        <td className="px-4 py-3 text-center">{r.removingDate}</td>
                        <td className="px-4 py-3 text-center">{r.removingHrKm}</td>
                        <td className="px-4 py-3 text-center">{r.life}</td>
                        <td className="px-4 py-3 text-center">{r.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col md:flex-row items-center justify-between gap-3 mt-5">
              <div className={`${isDark ? "text-gray-300" : "text-gray-700"} text-sm`}>
                Page <b>{page}</b> of <b>{totalPages}</b> • Total: <b>{total}</b>
              </div>

              <div className="flex items-center gap-3">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    const ps = Number(e.target.value || 10);
                    setPageSize(ps);
                    setPage(1);
                    loadList(filters, 1, ps);
                  }}
                  className={inputClass}
                  style={{ width: 120 }}
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n} / page
                    </option>
                  ))}
                </select>

                <button
                  type="button"
                  onClick={goPrev}
                  disabled={page <= 1 || loading}
                  className={`px-4 py-2 rounded-lg border ${
                    isDark
                      ? "border-gray-600 text-white hover:bg-gray-800"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Prev
                </button>

                <button
                  type="button"
                  onClick={goNext}
                  disabled={page >= totalPages || loading}
                  className={`px-4 py-2 rounded-lg border ${
                    isDark
                      ? "border-gray-600 text-white hover:bg-gray-800"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}
